import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, LogOut, Mail, Megaphone, Star, User } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Avatar } from "@/components/Layout";

export function MessagesLink() {
  const [count, setCount] = useState(0);
  const { pathname } = useLocation();
  useEffect(() => {
    const load = () => api.get("/messages/unread-count").then((r) => setCount(r.data.count)).catch(() => {});
    load();
    const t = setInterval(load, 20000);
    window.addEventListener("riff:messages-updated", load);
    return () => { clearInterval(t); window.removeEventListener("riff:messages-updated", load); };
  }, [pathname]);
  return (
    <Link to="/messages" data-testid="nav-messages-link" aria-label="Messages"
      className="relative rounded-sm p-2 text-gray-400 transition-colors hover:bg-white/5 hover:text-white">
      <Mail className="h-[18px] w-[18px]" strokeWidth={1.75} />
      {count > 0 && (
        <span data-testid="nav-unread-badge"
          className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-sm bg-[#FF2E4D] px-1 text-[10px] font-semibold tabular-nums text-white">
          {count > 9 ? "9+" : count}
        </span>
      )}
    </Link>
  );
}

export default function UserMenu() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const item = "cursor-pointer gap-2.5 rounded-sm text-[13px] text-gray-200 focus:bg-white/10 focus:text-white";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="desktop-nav-profile-link" className="rounded-full outline-none ring-offset-2 ring-offset-[#090A0C] focus-visible:ring-2 focus-visible:ring-white/40">
        <Avatar user={user} size="h-8 w-8" className="ring-1 ring-white/20" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 rounded-md border-[#2A2E37] bg-[#121418] p-1 text-white" data-testid="user-menu">
        <DropdownMenuLabel className="px-2 py-2">
          <p className="truncate text-[13px] font-semibold">{user.name}</p>
          <p className="truncate text-xs font-normal text-gray-500">@{user.username}</p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-[#2A2E37]" />
        <DropdownMenuItem className={item} onClick={() => nav(`/u/${user.username}`)} data-testid="menu-profile"><User className="h-4 w-4" />My page</DropdownMenuItem>
        {user.is_creator && (
          <DropdownMenuItem className={item} onClick={() => nav("/dashboard")} data-testid="menu-dashboard"><LayoutDashboard className="h-4 w-4" />Creator dashboard</DropdownMenuItem>
        )}
        {user.is_creator && (
          <DropdownMenuItem className={item} onClick={() => nav("/broadcast")} data-testid="menu-broadcast"><Megaphone className="h-4 w-4" />Mass message</DropdownMenuItem>
        )}
        <DropdownMenuItem className={item} onClick={() => nav("/subscriptions")} data-testid="menu-subscriptions"><Star className="h-4 w-4" />My subscriptions</DropdownMenuItem>
        <DropdownMenuItem className={item} onClick={() => nav("/messages")} data-testid="menu-messages"><Mail className="h-4 w-4" />Messages</DropdownMenuItem>
        <DropdownMenuSeparator className="bg-[#2A2E37]" />
        <DropdownMenuItem className={item} onClick={async () => { await logout(); nav("/"); }} data-testid="logout-button"><LogOut className="h-4 w-4" />Log out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
